import { Link } from 'react-router-dom'
import React from 'react'
import { EllipsisHorizontalIcon } from '@heroicons/react/24/outline'

import { Menu, MenuItem } from './ui'

import { BrandingDeployment, BrandingTemplate } from '../services/api'

/**
 * Settings -> Branding -> Deployments.
 *
 * A deployment is what devices actually received, which is a different question from what a branding
 * currently says. It carries a FROZEN copy of the appearance it shipped, so editing the branding
 * moves that on while the deployment keeps describing the estate. The two are listed apart for that
 * reason: one is what you are authoring, the other is what is out there.
 *
 * GROUPS ARE SHOWN, DEVICES ARE NOT, and the model is deliberate about it — group membership changes
 * constantly, so a stored device list is stale the moment it is written and reads as a record of
 * fact. What a given device holds is answered by its own marker
 * (HKLM\SOFTWARE\AEAM\Branding\Identity) against the identity below.
 */

const STATUS: Record<BrandingDeployment['status'], { label: string; tone: string; hint: string }> = {
  publishing: {
    label: 'Publishing',
    tone: 'text-amber-400 border-amber-400/40',
    hint: 'Being built, signed and put into Intune. It reaches no device until this finishes.',
  },
  published: {
    label: 'Published',
    tone: 'text-emerald-400 border-emerald-400/40',
    hint: 'In Intune and assigned. Devices pick it up on their next check-in.',
  },
  failed: {
    label: 'Failed',
    tone: 'text-red-400 border-red-400/40',
    hint: 'It did not reach Intune. The reason is on the row.',
  },
  withdrawing: {
    label: 'Removing',
    tone: 'text-blue-400 border-blue-400/40',
    hint: 'Assigned to uninstall. Devices restore the packaged branding on their next check-in; '
      + 'the Intune application is kept until then, because it is what removes it.',
  },
}

/** The application deployments table's own header cell, verbatim (pages/Deployments.tsx), so the
 *  two lists cannot drift into looking like different products. */
const thCls =
  'px-6 py-3 text-left text-xs font-medium text-theme-text-secondary uppercase tracking-wider'

const Pill: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] ${className ?? 'border-theme-border-primary text-theme-text-tertiary'}`}>
    {children}
  </span>
)

const BrandingDeploymentsPanel: React.FC<{
  deployments: BrandingDeployment[]
  templates: BrandingTemplate[]
  loading: boolean
  error: string | null
  onRemove?: (d: BrandingDeployment) => void
  /** Take the branding off the devices that hold it. THE ONLY action that does — removing the
   *  record does not, and the row says so rather than leaving it to be discovered. */
  onUninstall?: (d: BrandingDeployment) => void
  /** Change which groups receive it. The frozen copy of what shipped is untouched. */
  onReassign?: (d: BrandingDeployment) => void
  /** UNDEPLOY: uninstall from the audience, then take the application out of Intune. */
  onUndeploy?: (d: BrandingDeployment) => void
  busyId?: string | null
}> = ({ deployments, templates, loading, error, onRemove, onUninstall, onReassign, onUndeploy,
        busyId }) => {
  /** The branding a deployment came from, matched by IDENTITY. Matching on the template id would
   *  claim the deployment shows the current wording, which it does not — the identity is the content
   *  hash of what actually shipped. */
  const authoredAs = (d: BrandingDeployment) =>
    templates.find((t) => t.identity && t.identity === d.identity)

  return (
    <div className="rounded-lg border border-theme-border-primary bg-theme-secondary p-6">
      <h3 className="mb-1 text-lg font-semibold text-theme-text-primary">Deployments</h3>
      <p className="mb-4 text-xs text-theme-text-tertiary">
        What has been sent, and to which groups. A deployment keeps the appearance it shipped with,
        so editing a branding here never changes what a device already holds.
      </p>

      {loading && <p className="text-sm text-theme-text-tertiary">Loading…</p>}
      {error && <p className="text-sm text-red-400">{error}</p>}

      {!loading && !error && deployments.length === 0 && (
        <div className="rounded-lg border border-dashed border-theme-border-primary px-4 py-6">
          <p className="text-sm text-theme-text-secondary">Nothing deployed yet.</p>
          <p className="mt-1 text-xs text-theme-text-tertiary">
            Until a branding is deployed, dialogs show each application&apos;s own icon and PSADT&apos;s
            default wording. That is a working state, not a broken one.
          </p>
        </div>
      )}

      {/* THE SAME TABLE an application deployment is listed in (pages/Deployments.tsx): the same
          columns in the same order, the same header treatment, actions on the right. It was a stack
          of cards, so the one page an operator uses to see what has been sent to the estate read as
          two different products depending on which kind of thing had been sent. */}
      {!loading && !error && deployments.length > 0 && (
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-theme-border-primary">
        <thead className="bg-theme-tertiary">
          <tr>
            <th className={thCls}>Branding</th>
            <th className={thCls}>From</th>
            <th className={thCls}>Identity</th>
            <th className={thCls}>Assigned</th>
            <th className={thCls}>Status</th>
            <th className={`${thCls} text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-theme-border-primary">
        {deployments.map((d) => {
          const s = STATUS[d.status] ?? STATUS.publishing
          const from = authoredAs(d)
          return (
            <tr key={d.id} className="hover:bg-theme-tertiary/40">
              <td className="px-6 py-4">
                {/* The name is the way in to the deployment's own page, as an application
                    deployment's is: the per-device state it carries has no room in a table row. */}
                <Link
                  to={`/settings/branding/deployments/${d.id}`}
                  className="text-sm font-medium text-theme-text-primary hover:text-theme-accent-blue hover:underline"
                >
                  {d.name}
                </Link>
                {d.settings?.length > 0 && (
                  <p className="mt-1 text-xs text-theme-text-tertiary">
                    Takes over: <span className="text-theme-text-secondary">{d.settings.join(', ')}</span>
                  </p>
                )}
                {d.error && <p className="mt-1 text-xs text-red-400">{d.error}</p>}
              </td>

              {/* Named because a deployment outlives the branding that made it: an admin looking at
                  a device needs to know whether what is out there is still being authored. */}
              <td className="px-6 py-4 text-xs text-theme-text-tertiary">
                {from
                  ? <span className="text-theme-text-secondary">{from.name}</span>
                  : 'Edited or deleted since. Devices keep this one.'}
              </td>

              {/* What a device reports, so the registry marker on a machine can be compared against
                  it without going through the database. */}
              <td className="px-6 py-4 font-mono text-[11px] text-theme-text-tertiary">
                {d.identity ? d.identity.slice(0, 12) : '—'}
              </td>

              <td className="px-6 py-4 text-sm text-theme-text-primary">
                {d.group_ids?.length ?? 0} group{(d.group_ids?.length ?? 0) === 1 ? '' : 's'}
              </td>

              <td className="px-6 py-4">
                <Pill className={s.tone}>{s.label}</Pill>
                <p className="mt-1 max-w-xs text-[11px] text-theme-text-tertiary">{s.hint}</p>
              </td>

              <td className="px-6 py-4">
                {/* TWO ACTIONS THEN A DROPDOWN, as an application deployment has it
                    (pages/Deployments.tsx). Assign and Uninstall are the two an operator reaches
                    for; the ones that take the application out of the tenant, or drop our record
                    of it, sit behind the overflow where a mis-click on a busy row cannot fire
                    them. */}
                <div className="flex shrink-0 items-center justify-end gap-1.5">
                  {onReassign && (d.status === 'published' || d.status === 'withdrawing') && (
                    <button
                      type="button"
                      onClick={() => onReassign(d)}
                      disabled={busyId === d.id}
                      title="Change which groups receive this branding. What it contains is unchanged."
                      className="rounded-lg border border-theme-border-primary px-2.5 py-1.5 text-[13px] text-theme-text-primary hover:bg-theme-tertiary disabled:opacity-50"
                    >
                      {busyId === d.id ? 'Working…' : 'Assign'}
                    </button>
                  )}
                  {onUninstall && (d.status === 'published' || d.status === 'withdrawing') && (
                    <button
                      type="button"
                      onClick={() => onUninstall(d)}
                      disabled={busyId === d.id}
                      title="Take this branding off the devices that have it. The Intune application is kept, because it is what performs the removal."
                      className="rounded-lg border border-theme-border-primary px-2.5 py-1.5 text-[13px] text-theme-text-primary hover:bg-theme-tertiary disabled:opacity-50"
                    >
                      Uninstall
                    </button>
                  )}
                  <Menu
                    trigger={
                      <button
                        aria-label="More actions"
                        className="rounded-md p-1.5 text-theme-text-tertiary hover:bg-theme-tertiary hover:text-theme-text-primary"
                      >
                        <EllipsisHorizontalIcon className="h-4 w-4" />
                      </button>
                    }
                  >
                    {(close) => (
                      <>
                        {/* UNDEPLOY is Uninstall plus deleting the application, so it is one-way for
                            a device that has not checked in — which is why the plain Uninstall
                            stays outside, where it is easier to reach. */}
                        {onUndeploy && (d.status === 'published' || d.status === 'withdrawing') && (
                          <MenuItem
                            danger
                            onClick={() => {
                              onUndeploy(d)
                              close()
                            }}
                          >
                            Undeploy from Intune
                          </MenuItem>
                        )}
                        {onRemove && (
                          <MenuItem
                            danger
                            onClick={() => {
                              onRemove(d)
                              close()
                            }}
                          >
                            Remove deployment
                          </MenuItem>
                        )}
                      </>
                    )}
                  </Menu>
                </div>
              </td>
            </tr>
          )
        })}
        </tbody>
      </table>
      </div>
      )}
    </div>
  )
}

export default BrandingDeploymentsPanel
